import { useState } from "react";
import type { JSX, ReactNode } from "react";
import { Box, Button, CircularProgress, IconButton, TextField } from "@mui/material";
import BackspaceIcon from "@mui/icons-material/Backspace";

/**
 * Presentational phone keypad — a number field + 12-key grid + a submit button.
 *
 * Shared by the in-call `DialPad` (which dials the number into the live
 * conference) and the top-nav `CallLauncher` (which adds the number to the
 * recipients tray). The action label/icon/colour + the test-id prefix are
 * props, so each host gets its own affordance while the digit-entry logic +
 * the injection-safe input filter live in ONE place. Value is cleared on
 * submit so a host can collect several numbers in a row.
 */
const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"];

export function Keypad({
  onSubmit,
  submitLabel,
  submitBusyLabel,
  submitIcon,
  submitColor = "success",
  busy = false,
  minDigits = 3,
  testIdPrefix = "dialpad",
}: {
  onSubmit: (number: string) => void;
  submitLabel: string;
  /** Label shown while `busy` (e.g. "Dialing…"). Falls back to `submitLabel`. */
  submitBusyLabel?: string;
  submitIcon?: ReactNode;
  submitColor?: "success" | "primary" | "secondary";
  busy?: boolean;
  minDigits?: number;
  testIdPrefix?: string;
}): JSX.Element {
  const [value, setValue] = useState("");
  const canSubmit = value.replace(/\D/g, "").length >= minDigits && !busy;

  const append = (k: string): void => setValue((v) => (v + k).slice(0, 20));
  const backspace = (): void => setValue((v) => v.slice(0, -1));
  const submit = (): void => {
    if (!canSubmit) return;
    onSubmit(value.trim());
    setValue("");
  };

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
        <TextField
          value={value}
          onChange={(e) => setValue(e.target.value.replace(/[^\d+*#\s()-]/g, "").slice(0, 20))}
          onKeyDown={(e) => {
            if (e.key === "Enter") submit();
            e.stopPropagation(); // don't let an MUI Menu type-ahead steal digits
          }}
          placeholder="+1 555 0100"
          size="small"
          fullWidth
          inputProps={{ inputMode: "tel", "aria-label": "Phone number", "data-testid": `${testIdPrefix}-input` }}
        />
        <IconButton aria-label="Backspace" size="small" onClick={backspace} disabled={!value}>
          <BackspaceIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 0.75 }}>
        {KEYS.map((k) => (
          <Button
            key={k}
            data-testid={`${testIdPrefix}-key-${k}`}
            variant="outlined"
            onClick={() => append(k)}
            sx={{ minWidth: 0, py: 1, fontSize: 18, fontWeight: 600, color: "text.primary", borderColor: "divider" }}
          >
            {k}
          </Button>
        ))}
      </Box>
      <Button
        data-testid={`${testIdPrefix}-call`}
        fullWidth
        variant="contained"
        color={submitColor}
        startIcon={busy ? <CircularProgress size={16} color="inherit" /> : submitIcon}
        disabled={!canSubmit}
        onClick={submit}
        sx={{ mt: 1.25, borderRadius: 6 }}
      >
        {busy ? (submitBusyLabel ?? submitLabel) : submitLabel}
      </Button>
    </>
  );
}
